import { Component, input, output, forwardRef, signal, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-file-upload',
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div
        [class]="dropzoneClasses"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        (click)="triggerFileInput()">
        <input
          #fileInput
          type="file"
          [id]="id() || name()"
          [name]="name()"
          [multiple]="multiple()"
          [accept]="accept()"
          [disabled]="disabled()"
          class="hidden"
          (change)="onFileSelect($event)" />
        
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <div class="mt-4">
            <label class="cursor-pointer">
              <span class="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                {{ placeholder() }}
              </span>
              <span class="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                {{ helpText() || (multiple() ? 'or drag and drop multiple files' : 'or drag and drop a file') }}
              </span>
            </label>
          </div>
        </div>
      </div>
      
      @if (files().length > 0) {
        <div class="mt-2 space-y-2">
          @for (file of files(); track file.name) {
            <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ file.name }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatFileSize(file.size) }}</p>
                </div>
              </div>
              <button
                type="button"
                (click)="removeFile(file)"
                class="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          }
        </div>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class FileUploadComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('Click to upload or drag and drop');
  accept = input<string>('');
  multiple = input<boolean>(false);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  maxSize = input<number | undefined>(undefined);
  maxFiles = input<number | undefined>(undefined);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');

  fileSelectEvent = output<File[]>();

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  files = signal<File[]>([]);
  isDragging = signal(false);
  touched = signal(false);

  private onChange = (value: File[]) => {};
  private onTouched = () => {};

  triggerFileInput(): void {
    if (!this.disabled()) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelect(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.handleFiles(Array.from(target.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled()) {
      this.isDragging.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    
    if (this.disabled()) return;
    
    if (event.dataTransfer?.files) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  private handleFiles(newFiles: File[]): void {
    let validFiles = newFiles;
    
    // Check max files
    if (this.maxFiles() && this.files().length + validFiles.length > this.maxFiles()!) {
      validFiles = validFiles.slice(0, this.maxFiles()! - this.files().length);
    }
    
    // Check max size
    if (this.maxSize()) {
      validFiles = validFiles.filter(file => file.size <= this.maxSize()!);
    }
    
    const updatedFiles = this.multiple() 
      ? [...this.files(), ...validFiles]
      : validFiles.slice(0, 1);
    
    this.files.set(updatedFiles);
    this.onChange(updatedFiles);
    this.fileSelectEvent.emit(updatedFiles);
    this.markAsTouched();
  }

  removeFile(file: File): void {
    const updated = this.files().filter(f => f !== file);
    this.files.set(updated);
    this.onChange(updated);
    this.fileSelectEvent.emit(updated);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  get dropzoneClasses(): string {
    const classes = [
      'relative',
      'border-2',
      'border-dashed',
      'rounded-lg',
      'p-6',
      'text-center',
      'transition-colors',
      'duration-200'
    ];

    if (this.isDragging()) {
      classes.push('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'hover:border-gray-400', 'dark:hover:border-gray-500');
    }

    if (this.disabled()) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('cursor-pointer');
    }

    if (this.hasError) {
      classes.push('border-red-500');
    }

    return classes.join(' ');
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: File[]): void {
    this.files.set(value || []);
  }

  registerOnChange(fn: (value: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

